import type { LensCardData, LensFilterValues } from '~/types/leLens';

export const PRODUCT_PERFORMANCE_PAGE_ID = 'product-performance';

export type ReportView = 'product' | 'margin' | 'low-margin' | 'weekly' | 'comparisons';
export const REPORT_VIEWS: ReadonlyArray<{ value: ReportView; label: string }> = [
  { value: 'product', label: 'Product Performance' },
  { value: 'margin', label: 'Margin Performance' },
  { value: 'low-margin', label: 'Low Margin' },
  { value: 'weekly', label: 'Weekly Trends' },
  { value: 'comparisons', label: 'Period Comparisons' },
];

/** The workbook's filters, declared in svc-reporting's native-specs/product-performance.json and keyed by filter id. */
export const FILTER_DEFINITIONS = [
  { key: 'product', label: 'Product' },
  { key: 'destination_country', label: 'Destination country' },
  { key: 'opportunity', label: 'Opportunity' },
  { key: 'product_subcategory', label: 'Product sub-category' },
  { key: 'brand', label: 'Brand' },
  { key: 'item_status', label: 'Item status' },
  { key: 'transaction_type', label: 'Transaction type' },
  { key: 'destination_state', label: 'Destination state' },
  { key: 'destination_city', label: 'Destination city' },
  { key: 'customer_country', label: 'Customer country' },
  { key: 'vendor_parent', label: 'Vendor parent' },
  { key: 'vendor', label: 'Vendor' },
  { key: 'offer_id', label: 'Offer ID' },
  { key: 'market_manager', label: 'Offer market manager' },
  { key: 'pcm', label: 'PCM' },
  { key: 'luxplus_member', label: 'LuxPlus member' },
  { key: 'tactical', label: 'Tactical' },
  { key: 'charter_type', label: 'Charter type' },
  { key: 'order_currency', label: 'Order currency' },
] as const;
export type FilterKey = (typeof FILTER_DEFINITIONS)[number]['key'];
export const PRIMARY_FILTERS: ReadonlyArray<FilterKey> = ['product', 'destination_country', 'opportunity'];
const DEFAULT_ITEM_STATUS = ['completed', 'awaiting_dates'];
const DEFAULT_MARGIN_RANGE = { minMargin: 0, maxMargin: 0.098 };
const DEFAULT_WINDOW_DAYS = 30;

export type ProductFilters = {
  reportView: ReportView;
  from: string;
  to: string;
  minMargin: number;
  maxMargin: number;
  selections: Record<FilterKey, Array<string>>;
};

export type MetricKey =
  | 'sales'
  | 'margin'
  | 'marginRate'
  | 'totalMargin'
  | 'totalMarginRate'
  | 'packages'
  | 'orders'
  | 'nights'
  | 'adr'
  | 'leadTime'
  | 'los'
  | 'adults'
  | 'children'
  | 'infants';
export type ProductMetrics = Record<MetricKey, number | null>;
export type ProductGroup = {
  key: string;
  label: string;
  dimensions: Record<string, string | null>;
  metrics: ProductMetrics;
};
export type ProductPoint = { date: string; product: string; value: number | null; metrics: ProductMetrics };
export type CardResult = { rows: Array<Record<string, unknown>>; truncated: boolean; card: LensCardData };
export type CardProblem = { problem: string };

const SUMMED = [
  'sales_aud',
  'margin_aud',
  'total_margin_aud',
  'packages',
  'room_nights',
  'adults',
  'children',
  'infants',
  'adr_vendor_total_aud',
  'adr_known',
  'lead_time_total_days',
  'lead_time_known',
] as const;
type Sums = Record<(typeof SUMMED)[number], number | null>;
// The API returns the bucketed date under the column's own name; the Lens canvas renames it after shaping.
const DATE_KEYS = ['transaction_date_sydt', 'day(transaction_date_sydt)', 'week(transaction_date_sydt)'];

const sydneyDay = (date: Date) => new Intl.DateTimeFormat('en-CA', { timeZone: 'Australia/Sydney' }).format(date);
export const addDays = (day: string, amount: number) =>
  new Date(Date.parse(`${day}T00:00:00Z`) + amount * 86400000).toISOString().slice(0, 10);
export const monday = (day: string) => addDays(day, -((new Date(`${day}T00:00:00Z`).getUTCDay() + 6) % 7));
/** The last complete Sydney day, the newest date reporting.sales holds in full. */
export const lastCompleteDay = (now = new Date()) => addDays(sydneyDay(now), -1);

export function defaultFilters(reportView: ReportView = 'product', now = new Date()): ProductFilters {
  const to = lastCompleteDay(now);
  const selections = Object.fromEntries(FILTER_DEFINITIONS.map(({ key }) => [key, [] as Array<string>])) as Record<
    FilterKey,
    Array<string>
  >;
  return {
    reportView,
    from: addDays(to, 1 - DEFAULT_WINDOW_DAYS),
    to,
    ...DEFAULT_MARGIN_RANGE,
    selections: { ...selections, item_status: DEFAULT_ITEM_STATUS },
  };
}

const isDay = (value: string | null): value is string =>
  !!value && /^\d{4}-\d{2}-\d{2}$/.test(value) && new Date(`${value}T00:00:00Z`).toISOString().startsWith(value);

export function filtersError(filters: ProductFilters, now = new Date()): string | null {
  if (!isDay(filters.from) || !isDay(filters.to)) {
    return 'Choose valid dates in YYYY-MM-DD format.';
  }
  if (filters.from > filters.to) {
    return 'The start date must be on or before the end date.';
  }
  if (filters.to > lastCompleteDay(now)) {
    return `Sales are complete to ${lastCompleteDay(now)}. Choose an earlier end date.`;
  }
  if (
    !Number.isFinite(filters.minMargin) ||
    !Number.isFinite(filters.maxMargin) ||
    filters.minMargin > filters.maxMargin
  ) {
    return 'Choose a valid margin range.';
  }
  return null;
}

export function parseFilters(params: URLSearchParams, now = new Date()): ProductFilters {
  const requested = params.get('reportView');
  const reportView = REPORT_VIEWS.some((view) => view.value === requested) ? (requested as ReportView) : 'product';
  const filters = defaultFilters(reportView, now);
  if (isDay(params.get('from'))) {
    filters.from = params.get('from');
  }
  if (isDay(params.get('to'))) {
    filters.to = params.get('to');
  }
  for (const key of ['minMargin', 'maxMargin'] as const) {
    const value = Number(params.get(key));
    if (params.has(key) && Number.isFinite(value)) {
      filters[key] = value;
    }
  }
  for (const { key } of FILTER_DEFINITIONS) {
    if (!params.has(key)) {
      continue;
    }
    try {
      const value: unknown = JSON.parse(params.get(key));
      if (Array.isArray(value) && value.every((item) => typeof item === 'string')) {
        filters.selections[key] = value;
      }
    } catch {
      // A malformed selection keeps the default.
    }
  }
  return filters;
}

export function writeFilters(filters: ProductFilters, current: URLSearchParams, now = new Date()): URLSearchParams {
  const params = new URLSearchParams(current);
  const defaults = defaultFilters(filters.reportView, now);
  params.set('reportView', filters.reportView);
  for (const key of ['from', 'to', 'minMargin', 'maxMargin'] as const) {
    if (filters[key] === defaults[key]) {
      params.delete(key);
    } else {
      params.set(key, String(filters[key]));
    }
  }
  for (const { key } of FILTER_DEFINITIONS) {
    const values = [...new Set(filters.selections[key])].sort();
    if (JSON.stringify(values) === JSON.stringify([...defaults.selections[key]].sort())) {
      params.delete(key);
    } else {
      params.set(key, JSON.stringify(values));
    }
  }
  return params;
}

/**
 * The Lens request for the page's selection. An empty selection means every value; Item status
 * has a saved default, so clearing it sends every listed status rather than falling back to it.
 */
export function lensFilters(filters: ProductFilters, options: Record<string, Array<string>> = {}): LensFilterValues {
  const values: LensFilterValues = { period: { window: { from: filters.from, to: filters.to }, comparison: null } };
  for (const { key } of FILTER_DEFINITIONS) {
    const selected = filters.selections[key];
    if (selected.length) {
      values[key] = selected;
    } else if (key === 'item_status' && options.item_status?.length) {
      values[key] = options.item_status;
    }
  }
  return values;
}

const num = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = Number(typeof value === 'object' && value && 'value' in value ? value.value : value);
  return Number.isFinite(parsed) ? parsed : null;
};
const text = (value: unknown): string | null =>
  value === null || value === undefined
    ? null
    : String(typeof value === 'object' && 'value' in value ? value.value : value);
const divide = (a: number | null, b: number | null) => (a === null || b === null || b === 0 ? null : a / b);

function sumsOf(rows: Array<Record<string, unknown>>): Sums {
  return Object.fromEntries(
    SUMMED.map((key) => {
      const known = rows.map((row) => num(row[key])).filter((value): value is number => value !== null);
      return [key, known.length ? known.reduce((total, value) => total + value, 0) : null];
    }),
  ) as Sums;
}

/** Ratios and averages come from period sums, as the Tableau workbook computes them. */
export function metricsOf(sums: Sums, orders: number | null = null): ProductMetrics {
  return {
    sales: sums.sales_aud,
    margin: sums.margin_aud,
    marginRate: divide(sums.margin_aud, sums.sales_aud),
    totalMargin: sums.total_margin_aud,
    totalMarginRate: divide(sums.total_margin_aud, sums.sales_aud),
    packages: sums.packages,
    orders,
    nights: sums.room_nights,
    adr: divide(sums.adr_vendor_total_aud, sums.adr_known),
    leadTime: divide(sums.lead_time_total_days, sums.lead_time_known),
    los: divide(sums.room_nights, sums.packages),
    adults: sums.adults,
    children: sums.children,
    infants: sums.infants,
  };
}

export function readCard(cards: ReadonlyArray<LensCardData>, cardId: string): CardResult | CardProblem {
  const card = cards.find((item) => item.cardId === cardId);
  if (!card) {
    return { problem: 'This section is still loading.' };
  }
  if (card.status === 'ready' || card.status === 'empty') {
    return { rows: card.rows ?? [], truncated: Boolean(card.truncated), card };
  }
  if (card.status === 'restricted') {
    return { problem: card.message ?? 'You do not have access to this section.' };
  }
  return { problem: `This section could not load. ${card.message ?? ''}`.trim() };
}

export const isProblem = (result: CardResult | CardProblem): result is CardProblem => 'problem' in result;

export function summaryOf(result: CardResult): ProductMetrics {
  return metricsOf(sumsOf(result.rows));
}

/** One table row per value of `keys`, with distinct orders joined from a companion card when given. */
export function groupsOf(
  result: CardResult,
  keys: ReadonlyArray<string>,
  orders?: CardResult | CardProblem,
): Array<ProductGroup> {
  const ordersByLabel =
    orders && !isProblem(orders)
      ? new Map(orders.rows.map((row) => [text(row[keys[0]]) ?? 'Unknown', num(row.orders)]))
      : null;
  return result.rows.map((row) => {
    const dimensions = Object.fromEntries(keys.map((key) => [key, text(row[key])]));
    const label = keys.map((key) => dimensions[key] ?? 'Unknown').join(' · ');
    return {
      key: JSON.stringify(dimensions),
      label,
      dimensions,
      metrics: metricsOf(sumsOf([row]), ordersByLabel?.get(dimensions[keys[0]] ?? 'Unknown') ?? null),
    };
  });
}

/** Low Margin groups are vendor + booking + transaction time, sent as "BOOKING · YYYY-MM-DD HH:MM:SS". */
export function lowMarginGroups(result: CardResult, minMargin: number, maxMargin: number): Array<ProductGroup> {
  return result.rows
    .map((row) => {
      const bookingTransaction = text(row.booking_transaction) ?? 'Unknown';
      const cut = bookingTransaction.lastIndexOf(' · ');
      const dimensions = {
        vendor: text(row.vendor),
        booking: cut < 0 ? bookingTransaction : bookingTransaction.slice(0, cut),
        transactionDate: cut < 0 ? null : bookingTransaction.slice(cut + 3),
      };
      return {
        key: JSON.stringify(dimensions),
        label: Object.values(dimensions)
          .map((value) => value ?? 'Unknown')
          .join(' · '),
        dimensions,
        metrics: metricsOf(sumsOf([row])),
      };
    })
    .filter(
      (group) =>
        group.metrics.marginRate !== null &&
        group.metrics.marginRate >= minMargin &&
        group.metrics.marginRate <= maxMargin,
    )
    .sort((a, b) => a.label.localeCompare(b.label));
}

const dayRange = (from: string, to: string) => {
  const days: Array<string> = [];
  for (let day = from; day <= to && days.length < 1200; day = addDays(day, 1)) {
    days.push(day);
  }
  return days;
};
const weekRange = (from: string, to: string) => {
  const weeks: Array<string> = [];
  for (let week = monday(from); week <= to && weeks.length < 260; week = addDays(week, 7)) {
    weeks.push(week);
  }
  return weeks;
};

/**
 * Chart points per date and series, with every date in the card's window present so a missing
 * day is a gap rather than a skipped bar.
 */
export function pointsOf(
  result: CardResult,
  series: string | null,
  value: (metrics: ProductMetrics) => number | null,
  weekly = false,
): Array<ProductPoint> {
  const window = result.card.period?.current;
  const points = result.rows.map((row) => {
    const metrics = metricsOf(sumsOf([row]));
    return {
      date: (text(row[DATE_KEYS.find((key) => key in row) ?? DATE_KEYS[0]]) ?? '').slice(0, 10),
      product: series ? (text(row[series]) ?? 'Unknown') : 'All products',
      value: value(metrics),
      metrics,
    };
  });
  if (window?.from && window.to) {
    const seen = new Set(points.map((point) => `${point.date}\u0000${point.product}`));
    const members = [...new Set(points.map((point) => point.product))];
    if (!members.length && !series) {
      members.push('All products');
    }
    for (const date of weekly ? weekRange(window.from, window.to) : dayRange(window.from, window.to)) {
      for (const product of members) {
        if (!seen.has(`${date}\u0000${product}`)) {
          points.push({ date, product, value: null, metrics: metricsOf(sumsOf([])) });
        }
      }
    }
  }
  return points.sort((a, b) => a.date.localeCompare(b.date) || a.product.localeCompare(b.product));
}

/** Each product's share of the day's adjusted margin. */
export function marginShare(points: Array<ProductPoint>): Array<ProductPoint> {
  const totals = new Map<string, number>();
  for (const point of points) {
    if (point.metrics.margin !== null) {
      totals.set(point.date, (totals.get(point.date) ?? 0) + point.metrics.margin);
    }
  }
  return points.map((point) => ({ ...point, value: divide(point.metrics.margin, totals.get(point.date) ?? null) }));
}

export function periodLabel(card: LensCardData | undefined, fallback = ''): string {
  return card?.period?.label?.current ?? fallback;
}
